import { type FC } from 'react'
import { Box, Stack, Tooltip, Typography } from '@mui/material'

export interface StatusIconProps {
    disableTooltip?: boolean
}

const ICON_SIZE = 20

const CPI_LOGO_BASE64 =
    'iVBORw0KGgoAAAANSUhEUgAAACgAAAARCAYAAACvi+4IAAAD7UlEQVRIx+2UW4iVZRSGn/f7/v3vvWfGcczSMizKLK0x7SAV2VmpsLqJuhrwIqjwoiItuukEJXQ+QBGRnUOzq+iAgYFFEVEUVoimlik2NWU6o9vZh///3i6moYsiqpsu8oF1t/i+l7Xe9cJBDvLfor/buOyW5UyvD+vFnfPi/qKSF0nVTlJuU41B+WjHIU/7c9nqKC9C3tXJVJa2WjG4ncmtela2Zk5qFut3TU53VG7nple2/3OBF137EOsufF0z3xyotZ1NSYmji8QxhqNtjgRNASYbJgBdQA7kNplIOWCjEoVCuARaQFPQQAwLhoR3Snwn2JEpbXz01I+2P7llttc9vfzPBX628RkW3y16qqofaPsUo3MMx9pkvz3eQu6kRBsFA+NVTeYyo37hr0GrE+yNcleAzEBpEqYT5EziUNCJyZxt6ApiSx7SzUWZ1tYqoS/ZJ2fypiuO2Db4wc9H+NPnb0QLr3uYDXsmUct8UpG0DAjC66L8OXhy4bAgmdk2kwX1GBXGBSbTUyZmxMCbeWTFhCpb94xysc31En22KROtGFh91iGDKz/eM83VmGY2OmF1MiPAO8AmidNsFgJzJbbmIT02tat8qVWqrbnXPEMU+eD++HIgfdVX8/37WjQljm+V4ZHCmmMrAhaW8MSEukFEeVsl+p7ePK1Se6Q6nHqWtp0tMZogaAl+jMHv5SG9aHtvJ4ULkBaBPuqq8EpRFLVWiteV1o3JmjK+1iCP1GN5eUl4P1MqOaSrLIcatU1FUv/Po7om4MEQ1J5Q1ZrSvGFbSE5l6mkWLAEdlwW/Wgnlip7Y3l5QmxerE6d3l2W7ktJTzSIUMTizyZLVO1qEu0CzgvxlJfjB+dM6Gz79vnJGK2V3lmbRbwMY85xMEN8GMQQe8+CMgZXUc3XvHeXKIjGQrLmGPkkBbJAB246CbZXoew+tpzUjLQ5rFuG2Eg2A6mOWA9sCMiDDEAKNWvRTfXW/YGJj9wFf1Sm5KVmHj4sSNCW+ifJb9ZhWfvNVdfPCcxu/X/G1t93Hc5uP4oTeke59nez4Iun00jo1wSyb6UBvDKytqlix6vwPN1+9/pzz2ik8kMzpY3r8x3DQ2E0H0YmBn4AEuEz0AgckBoPYFvAXMfBJHtKGabXGUKOspM+eW/rXOXjJDU8wtbvUx9/X681Ck4LUW8u8I6XUqMbU/UuzMtBOmgPUgHrpIBuksZlHGcltwQHDfmA4iN2CoSB+yIKH8pB2T+8t9r27qb9469LHWbz8tX8f1OMsWLqKambtawVO23Wvj6n+qOGZS/Tsd/20CyNBmWD+YcNcnr/t+Qsu8plX3fpPvznI/4dfAa5D93SFenNsAAAAAElFTkSuQmCC'

const Dash: FC<{ label: string; disableTooltip?: boolean }> = ({
    label,
    disableTooltip,
}) => {
    const icon = (
        <span style={{ display: 'inline-flex' }}>
            <svg
                width={ICON_SIZE}
                height={ICON_SIZE}
                viewBox="0 0 15 15"
                fill="none"
            >
                <line
                    x1="4"
                    y1="7.5"
                    x2="11"
                    y2="7.5"
                    stroke="#cbd5e1"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                />
            </svg>
        </span>
    )
    return disableTooltip ? icon : <Tooltip title={label}>{icon}</Tooltip>
}

const Flag: FC<{ color: string; label: string; disableTooltip?: boolean }> = ({
    color,
    label,
    disableTooltip,
}) => {
    const icon = (
        <span style={{ display: 'inline-flex' }}>
            <svg
                width={ICON_SIZE}
                height={ICON_SIZE}
                viewBox="0 0 15 15"
                fill="none"
            >
                <path
                    d="M4 2V13.5"
                    stroke={color}
                    strokeWidth="1.4"
                    strokeLinecap="round"
                />
                <path d="M4 2.5H12L10 5.5L12 8.5H4" fill={color} />
            </svg>
        </span>
    )
    return disableTooltip ? icon : <Tooltip title={label}>{icon}</Tooltip>
}

export const recruitingStatusIcons: Record<string, FC<StatusIconProps>> = {
    'Final Stages': ({ disableTooltip }) => {
        const icon = (
            <span style={{ display: 'inline-flex' }}>
                <svg
                    width={ICON_SIZE}
                    height={ICON_SIZE}
                    viewBox="0 0 15 15"
                    fill="none"
                >
                    <defs>
                        <pattern
                            id="checkerboard"
                            width="4"
                            height="4"
                            patternUnits="userSpaceOnUse"
                        >
                            <rect width="2" height="2" fill="#1e293b" />
                            <rect
                                x="2"
                                y="2"
                                width="2"
                                height="2"
                                fill="#1e293b"
                            />
                            <rect x="2" width="2" height="2" fill="#fff" />
                            <rect y="2" width="2" height="2" fill="#fff" />
                        </pattern>
                    </defs>
                    <path
                        d="M4 2V13.5"
                        stroke="#1e293b"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                    />
                    <path
                        d="M4 2.5H12L10 5.5L12 8.5H4"
                        fill="url(#checkerboard)"
                    />
                </svg>
            </span>
        )
        return disableTooltip ? (
            icon
        ) : (
            <Tooltip title="Recruiting: Final Stages">{icon}</Tooltip>
        )
    },
    'Actively Recruiting': ({ disableTooltip }) => (
        <Flag
            color="#16a34a"
            label="Recruiting: Actively Recruiting"
            disableTooltip={disableTooltip}
        />
    ),
    Opportunistic: ({ disableTooltip }) => (
        <Flag
            color="#facc15"
            label="Recruiting: Opportunistic"
            disableTooltip={disableTooltip}
        />
    ),
    'Holding Out': ({ disableTooltip }) => (
        <Flag
            color="#facc15"
            label="Recruiting: Holding Out"
            disableTooltip={disableTooltip}
        />
    ),
    'Not Recruiting': ({ disableTooltip }) => (
        <Flag
            color="#dc2626"
            label="Recruiting: Not Recruiting"
            disableTooltip={disableTooltip}
        />
    ),
    'Do Not Call': ({ disableTooltip }) => (
        <Flag
            color="#dc2626"
            label="Recruiting: Do Not Call"
            disableTooltip={disableTooltip}
        />
    ),
    'Red Flag': ({ disableTooltip }) => (
        <Flag
            color="#dc2626"
            label="Recruiting: Red Flag"
            disableTooltip={disableTooltip}
        />
    ),
    'No Status': ({ disableTooltip }) => (
        <Dash label="Recruiting: No Status" disableTooltip={disableTooltip} />
    ),
}

export const outreachStatusIcons: Record<string, FC<StatusIconProps>> = {
    'No Status': ({ disableTooltip }) => (
        <Dash label="Off Limits: No Status" disableTooltip={disableTooltip} />
    ),
    'Non-Poach': ({ disableTooltip }) => {
        const icon = (
            <span style={{ display: 'inline-flex' }}>
                <svg
                    width={ICON_SIZE}
                    height={ICON_SIZE}
                    viewBox="0 0 15 15"
                    fill="none"
                >
                    <path
                        d="M7.5 1.5L14 13H1L7.5 1.5Z"
                        stroke="#b91c1c"
                        strokeWidth="1.3"
                        fill="none"
                        strokeLinejoin="round"
                    />
                    <line
                        x1="5.5"
                        y1="5.5"
                        x2="9.5"
                        y2="10.5"
                        stroke="#b91c1c"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                    />
                </svg>
            </span>
        )
        return disableTooltip ? (
            icon
        ) : (
            <Tooltip title="Off Limits: Non-Poach">{icon}</Tooltip>
        )
    },
    'Tread Carefully': ({ disableTooltip }) => {
        const icon = (
            <span style={{ display: 'inline-flex' }}>
                <svg
                    width={ICON_SIZE}
                    height={ICON_SIZE}
                    viewBox="0 0 15 15"
                    fill="none"
                >
                    <path
                        d="M7.5 1.5L14 13H1L7.5 1.5Z"
                        fill="#b45309"
                        strokeLinejoin="round"
                    />
                    <line
                        x1="7.5"
                        y1="6"
                        x2="7.5"
                        y2="9"
                        stroke="#fff"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                    />
                    <circle cx="7.5" cy="10.8" r="0.8" fill="#fff" />
                </svg>
            </span>
        )
        return disableTooltip ? (
            icon
        ) : (
            <Tooltip title="Off Limits: Tread Carefully">{icon}</Tooltip>
        )
    },
    'CPI Placed': ({ disableTooltip }) => {
        const icon = (
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: ICON_SIZE,
                    height: ICON_SIZE,
                }}
            >
                <img
                    src={`data:image/png;base64,${CPI_LOGO_BASE64}`}
                    width="30"
                    height="16"
                    style={{ display: 'block' }}
                    alt="CPI"
                />
            </span>
        )
        return disableTooltip ? (
            icon
        ) : (
            <Tooltip title="Off Limits: CPI Placed">{icon}</Tooltip>
        )
    },
}

const CheckedBox: FC<{ label: string; disableTooltip?: boolean }> = ({
    label,
    disableTooltip,
}) => {
    const icon = (
        <span style={{ display: 'inline-flex' }}>
            <svg
                width={ICON_SIZE}
                height={ICON_SIZE}
                viewBox="0 0 15 15"
                fill="none"
            >
                <rect
                    x="1.5"
                    y="1.5"
                    width="12"
                    height="12"
                    rx="2"
                    fill="#0f172a"
                    stroke="#0f172a"
                    strokeWidth="1.3"
                />
                <path
                    d="M4.5 7.5L6.5 9.5L10.5 5.5"
                    stroke="#fff"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </span>
    )
    return disableTooltip ? icon : <Tooltip title={label}>{icon}</Tooltip>
}

const EmptyBox: FC<{ label: string; disableTooltip?: boolean }> = ({
    label,
    disableTooltip,
}) => {
    const icon = (
        <span style={{ display: 'inline-flex' }}>
            <svg
                width={ICON_SIZE}
                height={ICON_SIZE}
                viewBox="0 0 15 15"
                fill="none"
            >
                <rect
                    x="1.5"
                    y="1.5"
                    width="12"
                    height="12"
                    rx="2"
                    stroke="#cbd5e1"
                    strokeWidth="1.3"
                    fill="#fff"
                />
            </svg>
        </span>
    )
    return disableTooltip ? icon : <Tooltip title={label}>{icon}</Tooltip>
}

export const systemStatusIcons: Record<string, FC<StatusIconProps>> = {
    null: ({ disableTooltip }) => (
        <Dash label="Registration: No Status" disableTooltip={disableTooltip} />
    ),
    Met: ({ disableTooltip }) => (
        <CheckedBox label="Registration: Met" disableTooltip={disableTooltip} />
    ),
    'In Process': ({ disableTooltip }) => (
        <CheckedBox
            label="Registration: In Process"
            disableTooltip={disableTooltip}
        />
    ),
    'Sent Scheduler': ({ disableTooltip }) => (
        <CheckedBox
            label="Registration: Sent Scheduler"
            disableTooltip={disableTooltip}
        />
    ),
    Registered: ({ disableTooltip }) => (
        <EmptyBox
            label="Registration: Registered"
            disableTooltip={disableTooltip}
        />
    ),
    'Emailed Invitation': ({ disableTooltip }) => (
        <EmptyBox
            label="Registration: Emailed Invitation"
            disableTooltip={disableTooltip}
        />
    ),
    Unregistered: ({ disableTooltip }) => (
        <EmptyBox
            label="Registration: Unregistered"
            disableTooltip={disableTooltip}
        />
    ),
    'On Hold': ({ disableTooltip }) => (
        <EmptyBox
            label="Registration: On Hold"
            disableTooltip={disableTooltip}
        />
    ),
    'No Status': ({ disableTooltip }) => (
        <Dash label="Registration: No Status" disableTooltip={disableTooltip} />
    ),
}

const LEGEND_ICON_SIZE = 20

const LegendFlag: FC<{ color: string }> = ({ color }) => (
    <svg
        width={LEGEND_ICON_SIZE}
        height={LEGEND_ICON_SIZE}
        viewBox="0 0 15 15"
        fill="none"
    >
        <path
            d="M4 2V13.5"
            stroke={color}
            strokeWidth="1.4"
            strokeLinecap="round"
        />
        <path d="M4 2.5H12L10 5.5L12 8.5H4" fill={color} />
    </svg>
)

export const StatusIconLegend: FC = () => {
    const categories = [
        {
            title: 'RECRUITING',
            desc: 'Colored flags',
            shape: (
                <svg width="18" height="16" viewBox="0 0 15 15" fill="none">
                    <path
                        d="M4 2V13.5"
                        stroke="#94a3b8"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                    />
                    <path d="M4 2.5H12L10 5.5L12 8.5H4" fill="#94a3b8" />
                </svg>
            ),
            items: [
                { label: 'Final Stages', icon: <LegendFlag color="#1e293b" /> },
                {
                    label: 'Actively Recruiting',
                    icon: <LegendFlag color="#16a34a" />,
                },
                {
                    label: 'Opportunistic / Holding Out',
                    icon: <LegendFlag color="#facc15" />,
                },
                {
                    label: 'Not Recruiting / Do Not Call / Red Flag',
                    icon: <LegendFlag color="#dc2626" />,
                },
            ],
        },
        {
            title: 'OFF LIMITS',
            desc: 'Warning shapes + CPI logo',
            shape: (
                <svg width="16" height="16" viewBox="0 0 12 12">
                    <path
                        d="M6 1L11 11H1Z"
                        stroke="#94a3b8"
                        strokeWidth="1"
                        fill="none"
                        strokeLinejoin="round"
                    />
                </svg>
            ),
            items: [
                {
                    label: 'Non-Poach',
                    icon: (
                        <svg
                            width={LEGEND_ICON_SIZE}
                            height={LEGEND_ICON_SIZE}
                            viewBox="0 0 15 15"
                            fill="none"
                        >
                            <path
                                d="M7.5 1.5L14 13H1L7.5 1.5Z"
                                stroke="#b91c1c"
                                strokeWidth="1.3"
                                fill="none"
                                strokeLinejoin="round"
                            />
                            <line
                                x1="5.5"
                                y1="5.5"
                                x2="9.5"
                                y2="10.5"
                                stroke="#b91c1c"
                                strokeWidth="1.4"
                                strokeLinecap="round"
                            />
                        </svg>
                    ),
                },
                {
                    label: 'Tread Carefully',
                    icon: (
                        <svg
                            width={LEGEND_ICON_SIZE}
                            height={LEGEND_ICON_SIZE}
                            viewBox="0 0 15 15"
                            fill="none"
                        >
                            <path
                                d="M7.5 1.5L14 13H1L7.5 1.5Z"
                                fill="#b45309"
                                strokeLinejoin="round"
                            />
                            <line
                                x1="7.5"
                                y1="6"
                                x2="7.5"
                                y2="9"
                                stroke="#fff"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                            />
                            <circle cx="7.5" cy="10.8" r="0.8" fill="#fff" />
                        </svg>
                    ),
                },
                {
                    label: 'CPI Placed',
                    icon: (
                        <img
                            src={`data:image/png;base64,${CPI_LOGO_BASE64}`}
                            width="26"
                            height="12"
                            style={{ display: 'block' }}
                            alt="CPI"
                        />
                    ),
                },
            ],
        },
        {
            title: 'REGISTRATION',
            desc: 'Checkbox statuses',
            shape: (
                <svg width="16" height="16" viewBox="0 0 15 15" fill="none">
                    <rect
                        x="1.5"
                        y="1.5"
                        width="12"
                        height="12"
                        rx="2"
                        stroke="#94a3b8"
                        strokeWidth="1.3"
                    />
                </svg>
            ),
            items: [
                {
                    label: 'Met / In Process / Sent Scheduler',
                    icon: (
                        <svg
                            width={LEGEND_ICON_SIZE}
                            height={LEGEND_ICON_SIZE}
                            viewBox="0 0 15 15"
                            fill="none"
                        >
                            <rect
                                x="1.5"
                                y="1.5"
                                width="12"
                                height="12"
                                rx="2"
                                fill="#0f172a"
                                stroke="#0f172a"
                                strokeWidth="1.3"
                            />
                            <path
                                d="M4.5 7.5L6.5 9.5L10.5 5.5"
                                stroke="#fff"
                                strokeWidth="1.6"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    ),
                },
                {
                    label: 'Registered / Emailed Invitation / Unregistered',
                    icon: (
                        <svg
                            width={LEGEND_ICON_SIZE}
                            height={LEGEND_ICON_SIZE}
                            viewBox="0 0 15 15"
                            fill="none"
                        >
                            <rect
                                x="1.5"
                                y="1.5"
                                width="12"
                                height="12"
                                rx="2"
                                stroke="#cbd5e1"
                                strokeWidth="1.3"
                                fill="#fff"
                            />
                        </svg>
                    ),
                },
            ],
        },
    ]

    return (
        <Box
            sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: '#fff',
            }}
        >
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
                Icon Legend
            </Typography>
            <Stack direction="row" gap={4}>
                {categories.map(({ title, desc, shape, items }) => (
                    <Stack key={title} gap={1}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            gap={0.75}
                            sx={{
                                pb: 1,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                            }}
                        >
                            {shape}
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: 10,
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.08em',
                                        color: 'text.secondary',
                                    }}
                                >
                                    {title}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: 9.5,
                                        color: 'text.disabled',
                                        mt: 0.125,
                                    }}
                                >
                                    {desc}
                                </Typography>
                            </Box>
                        </Stack>
                        {items.map((item, idx) => (
                            <Stack
                                key={idx}
                                direction="row"
                                alignItems="center"
                                gap={1}
                            >
                                {item.icon}
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        color: 'text.secondary',
                                        fontWeight: 500,
                                    }}
                                >
                                    {item.label}
                                </Typography>
                            </Stack>
                        ))}
                        <Stack direction="row" alignItems="center" gap={1}>
                            <svg
                                width={LEGEND_ICON_SIZE}
                                height={LEGEND_ICON_SIZE}
                                viewBox="0 0 15 15"
                                fill="none"
                            >
                                <line
                                    x1="4"
                                    y1="7.5"
                                    x2="11"
                                    y2="7.5"
                                    stroke="#cbd5e1"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                />
                            </svg>
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    color: 'text.disabled',
                                    fontWeight: 500,
                                }}
                            >
                                No Status
                            </Typography>
                        </Stack>
                    </Stack>
                ))}
            </Stack>
        </Box>
    )
}
